import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import { ColorSchemes } from '../ChartOptions/Color/ColorScheme';
import { ChartsMapStateToProps, ChartsReduxStateProps } from './ChartsCommonInterfaces';
import EmptyQuery from './EmptyQuery';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { formatTooltipValueForSelectedMeasure } from '../ChartOptions/Format/NumberFormatter';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { limitChartData, shouldChartDisplayWithSecurity } from '../CommonFunctions/CommonFunctions';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import { LABEL_THRESHOLDS, MARGIN_THRESHOLDS } from '../ChartOptions/ChartThresholds';

const Sankey = ({
  // props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  softUI,
  fontFamily,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  var colorSchems = ColorSchemes[0].colors;

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (
      !chartControl.colorScheme ||
      (chartArea === 'dashboard' && !chartControl.sankeyControls.manualColor)
    ) {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const { tabState, selectedTab, showDash, chartAxes, chartType } = useSelector(
    (state: RootState) => ({
      tabState: state.tabState,
      selectedTab: state.tabTileProps.selectedTabId,
      showDash: state.tabTileProps.showDash,
      chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
      chartType: state.chartProperties?.properties[propKey]?.chartType,
    }),
  );
  const dispatch = useDispatch();

  const [nodes, setNodes] = useState<any>([]);
  const [links, setLinks] = useState<any>([]);

  var dimensionsKeys: string[] | any = [];
  var measure: string = '';
  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;
  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);

  const limitedChartData = limitChartData(chartData, 5000, chartAxes[2]?.fields[0]?.displayname);

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length > 5000 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 5000 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length >= 1) {
      dimensionsKeys = chartProperties.properties[propKey].chartAxes[1].fields.map(
        (el: any) => el.displayname,
      );
      // if ("timeGrain" in el) {
      // 	return `${el.timeGrain} of ${el.fieldname}`;
      // } else if ("agg" in el) {
      // 	return `${el.agg} of ${el.fieldname} `;
      // } else {
      // 	return el.fieldname;
      // }

      //getting measure value as string since allowed numof measure is 1 for this chart

      chartProperties.properties[propKey].chartAxes[2].fields.forEach((el: any) => {
        // measure = `${el.fieldname}__${el.agg}`;
        // measure = `${el.agg} of ${el.fieldname}`;
        measure = el.displayname;
      });

      const getColorOfNode = (nodeName: string, idx: number) => {
        let color = '';

        if (chartControl.sankeyControls.manualColor) {
          const foundNode = chartControl.sankeyControls.nodesAndColors.find(
            (el) => el.nodeName === nodeName,
          );
          if (foundNode) {
            color = foundNode.nodeColor;
          }
        }

        if (!color) {
          color = selectedTheme?.colors?.[idx] ?? colorSchems[idx];
        }

        return color;
      };

      //getting values for data in series
      var finalValuesOfNode: any = [];
      dimensionsKeys.forEach((element: any, i: number) => {
        var allValues = limitedChartData.map((dt: any) => dt[element]);
        var uniqueValues = [...new Set(allValues)];

        uniqueValues = uniqueValues.map((el) => {
          return {
            name: el + ' ' + i,
            label: {
              formatter: el + '',
              position:
                chartControl.sankeyControls.labelPosition === 'inside'
                  ? i / 2 === 0
                    ? [30 + chartControl.sankeyControls.labelDistance, 10]
                    : [-70 - 2 * (chartControl.sankeyControls.labelDistance / 2), 10]
                  : i / 2 === 0
                    ? [-70 - 2 * (chartControl.sankeyControls.labelDistance / 2), 10]
                    : [30 + chartControl.sankeyControls.labelDistance, 10],
              show:
                Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                  ? chartControl.labelOptions.showLabel
                  : false,
              fontSize: chartControl.labelOptions.fontSize,
              fontFamily: fontFamily,
              color: chartControl.labelOptions.labelColor
                ? chartControl.labelOptions.labelColor
                : selectedTheme?.dark
                  ? '#ffffff'
                  : '#757474',
              backgroundColor: chartControl.labelOptions.labelBackground
                ? chartControl.labelOptions.labelBackgroundColor
                : 'transparent',
              borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
              padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,

              overflow: chartControl.sankeyControls.overFlow,
              distance: chartControl.sankeyControls.labelDistance,
              rotate: chartControl.sankeyControls.labelRotate,
              verticalAlign: 'top',
            },
            itemStyle: {
              color:
                chartControl.sankeyControls.nodesAndColors.length !== 0
                  ? getColorOfNode(element, i)
                  : (selectedTheme?.colors?.[i] ?? colorSchems[i]),
            },
          };
        });

        finalValuesOfNode.push(...uniqueValues);
      });

      setNodes(finalValuesOfNode);

      //getting values for links in series
      let valuesOfLink = [];

      for (var i = 0; i < dimensionsKeys.length - 1; i++) {
        valuesOfLink = limitedChartData.map((el: any) => {
          var obj: any = {};
          obj.source = el[dimensionsKeys[i]] + ' ' + 0;
          // typeof el[dimensionsKeys[i]] === "number"
          //   ? JSON.stringify(el[dimensionsKeys[i]])
          //   : el[dimensionsKeys[i]];
          obj.target = el[dimensionsKeys[i + 1]] + ' ' + 1;
          // typeof el[dimensionsKeys[i + 1]] === "number"
          //   ? JSON.stringify(el[dimensionsKeys[i + 1]])
          //   : el[dimensionsKeys[i + 1]];
          obj.value = el[measure];
          obj.label = el[dimensionsKeys[i]] + ' -- ' + el[dimensionsKeys[i + 1]];
          obj.lineStyle = {
            color: chartControl.sankeyControls.manualColor
              ? chartControl.sankeyControls.linkColor
              : chartThemes[0]?.dark
                ? '#ffffff'
                : '#666666',
            opacity: chartControl.sankeyControls.opacity / 100,
            curveness: chartControl.sankeyControls.curveness / 100,
          };
          return obj;
        });
      }
      setLinks(valuesOfLink);
    }
  }, [chartData, chartControl, graphDimension]);

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const hoveredItems = tabState.tabs[selectedTab].hoveredItems || [];
      const dimKeys = chartDataKeys;

      const sourceIndex = nodes.findIndex((node: any) =>
        hoveredItems?.some(
          (hovered: Record<string, any>) =>
            node.name.replace(/\s\d+$/, '') === String(hovered[dimKeys[0]]),
        ),
      );
      const targetIndex = nodes.findIndex((node: any) =>
        hoveredItems?.some(
          (hovered: Record<string, any>) =>
            node.name.replace(/\s\d+$/, '') === String(hovered[dimKeys[1]]),
        ),
      );

      const edgeIndex = links.findIndex((link: any) => {
        const linkSource = link.source.replace(/\s\d+$/, '');
        const linkTarget = link.target.replace(/\s\d+$/, '');

        // Loop over all unique pairs in hoveredItems
        for (let i = 0; i < hoveredItems.length; i++) {
          for (let j = 0; j < hoveredItems.length; j++) {
            if (i === j) continue;

            const sourceVal = String(hoveredItems[i][dimKeys[0]]);
            const targetVal = String(hoveredItems[j][dimKeys[1]]);

            if (
              (linkSource === sourceVal && linkTarget === targetVal) ||
              (linkSource === targetVal && linkTarget === sourceVal)
            ) {
              return true;
            }
          }
        }

        return false;
      });

      if (sourceIndex >= 0 && targetIndex >= 0) {
        if (edgeIndex !== -1) {
          chartInstance.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataType: 'edge',
            dataIndex: edgeIndex,
          });
        }
      } else if (sourceIndex >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: sourceIndex,
        });
      } else if (targetIndex >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: targetIndex,
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

      hoverdTimeout.current = setTimeout(() => {
        if (
          params?.dataType === 'node' &&
          params?.name &&
          hoveredRef.current?.[chartDataKeys[0]] !== params.name.replace(/\s\d+$/, '') &&
          hoveredRef.current?.[chartDataKeys[1]] !== params.name.replace(/\s\d+$/, '')
        ) {
          const newEntry = {
            [chartDataKeys[parseInt(params.name.match(/\d+$/)[0])]]: params.name.replace(
              /\s\d+$/,
              '',
            ),
          };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some(
              (item) =>
                item[chartDataKeys[parseInt(params.name.match(/\d+$/)[0])]] ===
                params.name.replace(/\s\d+$/, ''),
            );
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }
        if (
          params?.dataType === 'edge' &&
          params?.name &&
          hoveredRef.current?.[chartDataKeys[0]] !== params.data.source.replace(/\s\d+$/, '') &&
          hoveredRef.current?.[chartDataKeys[1]] !== params.data.target.replace(/\s\d+$/, '')
        ) {
          const newEntry_1 = {
            [chartDataKeys[parseInt(params.data.source.match(/\d+$/)[0])]]:
              params.data.source.replace(/\s\d+$/, ''),
          };
          const newEntry_2 = {
            [chartDataKeys[parseInt(params.data.target.match(/\d+$/)[0])]]:
              params.data.target.replace(/\s\d+$/, ''),
          };
          hoveredRef.current = newEntry_1;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some(
              (item) => item[chartDataKeys[0]] === params.data.source.replace(/\s\d+$/, ''),
            );
            if (!isDuplicate) {
              return [...prev, newEntry_1, newEntry_2];
            }
            return prev;
          });
        }
      }, 200);
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const isTooltipEnabled =
    chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable;

  const RenderChart = () => {
    return (
      <>
        <ReactEcharts
          opts={{ renderer: 'svg' }}
          ref={chartRef}
          onChartReady={handleChartReady}
          // theme={chartControl.colorScheme}
          style={{
            padding: '5px',
            width: graphDimension.width,
            height: graphDimension.height,
            overflow: 'hidden',
            margin: 'auto',
            border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
          }}
          // 	series: [
          // 		{
          // 			type: "treemap",
          //
          // 			label: {
          // 				show: chartControl.labelOptions.showLabel,
          // 				fontSize: chartControl.labelOptions.fontSize,
          // 				color: chartControl.labelOptions.labelColorManual
          // 					? chartControl.labelOptions.labelColor
          // 					: null,
          // 				position: chartControl.treeMapChartControls.labelPosition,
          // 				rotate: chartControl.treeMapChartControls.labelRotate,
          // 				align: chartControl.treeMapChartControls.labelRotate,
          // 				verticalAlign: chartControl.treeMapChartControls.horizondalAlign,
          // 				overflow: chartControl.treeMapChartControls.overFlow,
          // 			},
          // 			itemStyle: {
          // 				borderWidth: chartControl.treeMapChartControls.borderWidth,
          // 				gapWidth: chartControl.treeMapChartControls.gapWidth,
          // 				borderColorSaturation: 1,
          // 			},
          // 			breadcrumb: {
          // 				show: chartControl.treeMapChartControls.showBreadCrumb,
          // 				height: chartControl.treeMapChartControls.bcHeight,
          // 				emptyItemWidth: chartControl.treeMapChartControls.bcWidth,
          // 				itemStyle: {
          // 					color: chartControl.treeMapChartControls.bcColor,
          // 				},
          // 			},
          // 			leafDepth: chartControl.treeMapChartControls.leafDepth,
          // 			data: sourceData,
          // 		},
          // 	],
          // }}

          option={{
            color: selectedTheme?.colors || [],
            animation: false,
            backgroundColor: chartControl.sankeyControls.manualColor
              ? chartControl.sankeyControls.backgroundColor
              : selectedTheme?.background || [],
            tooltip: {
              show: isTooltipEnabled,
              trigger: 'item',
              triggerOn: 'mousemove',
              appendToBody: true,
              textStyle: {
                fontFamily: fontFamily,
              },
              formatter: function (params: any) {
                // Check if it's a node
                if (params.dataType === 'node') {
                  return params.data.label.formatter; // Return the custom label
                }
                // Check if it's a link
                if (params.dataType === 'edge') {
                  // --------------for label -------------//
                  const tooltipFormats =
                    chartControls?.properties[propKey]?.formatOptions?.tooltipFormats;

                  let formattedLabel = '';
                  const splittedData = params.data.label.split('--');

                  splittedData.forEach((data: any, index: number) => {
                    const dimensionAxis = chartProperties.properties[propKey].chartAxes[1];
                    const currentUid = dimensionAxis.fields[index].uId;
                    const formattedKey = formatTooltipValueForSelectedMeasure(
                      currentUid,
                      tooltipFormats,
                      data.trim(),
                    );
                    formattedLabel +=
                      index < splittedData.length - 1 ? formattedKey : ` -- ${formattedKey}`;
                  });
                  //--------------for measure ----------------//
                  const measureAxis = chartProperties.properties[propKey].chartAxes.find(
                    (axis: any) => axis.name === 'Measure',
                  );
                  const currentUid = measureAxis.fields[0].uId;
                  const formattedValue = formatTooltipValueForSelectedMeasure(
                    currentUid,
                    tooltipFormats,
                    params.data.value,
                  );
                  return `${formattedLabel} : ${formattedValue}`;
                }
              },
            },
            series: [
              {
                type: 'sankey',
                layoutIterations: 0,
                left:
                  Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                    ? 0
                    : chartControl.chartMargin.left + '%',
                right:
                  Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                    ? 0
                    : chartControl.chartMargin.right + '%',
                top:
                  Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                    ? 0
                    : chartControl.chartMargin.top + '%',
                bottom:
                  Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                    ? 0
                    : chartControl.chartMargin.bottom + '%',
                nodeWidth: chartControl.sankeyControls.nodeWidth,
                nodeGap: chartControl.sankeyControls.nodeGap,
                nodeAlign: chartControl.sankeyControls.nodeAlign,
                orient: chartControl.sankeyControls.orient,
                draggable: chartControl.sankeyControls.draggable,
                //  width: 100 - (chartControl.chartMargin.left + chartControl.chartMargin.right) + '%',
                //  height: 100 - (chartControl.chartMargin.top + chartControl.chartMargin.bottom) + '%',
                // label: {
                // 	show: chartControl.labelOptions.showLabel,
                // 	fontSize: chartControl.labelOptions.fontSize,
                // 	color: chartControl.labelOptions.labelColorManual
                // 		? chartControl.labelOptions.labelColor
                // 		: null,
                // 	align: chartControl.sankeyControls.horizondalAlign,
                // 	verticalAlign: chartControl.sankeyControls.verticalAlign,
                // 	overflow: chartControl.sankeyControls.overFlow,
                // 	distance: chartControl.sankeyControls.labelDistance,
                // 	rotate: chartControl.sankeyControls.labelRotate,
                // },
                data: nodes,
                links: links,
                emphasis: {
                  focus: 'adjacency',
                },
                levels: [
                  {
                    depth: 0,
                    itemStyle: {
                      color: '#fbb4ae',
                    },
                    lineStyle: {
                      color: 'source',
                      opacity: 0.6,
                    },
                  },
                  {
                    depth: 1,
                    itemStyle: {
                      color: '#b3cde3',
                    },
                    lineStyle: {
                      color: 'source',
                      opacity: 0.6,
                    },
                  },
                  {
                    depth: 2,
                    itemStyle: {
                      color: '#ccebc5',
                    },
                    lineStyle: {
                      color: 'source',
                      opacity: 0.6,
                    },
                  },
                  {
                    depth: 3,
                    itemStyle: {
                      color: '#decbe4',
                      ...(softUI
                        ? {
                            shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
                            shadowBlur: 10, // Shadow blur
                            shadowOffsetX: 3, // Horizontal shadow offset
                            shadowOffsetY: 3,
                          }
                        : {}),
                    },
                    lineStyle: {
                      color: 'source',
                      opacity: 0.6,
                    },
                  },
                ],
                lineStyle: {
                  curveness: 0.5,
                },
              },
            ],
          }}
        />
      </>
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : chartData.length >= 1 &&
    chartProperties.properties[propKey].chartAxes[1].fields.length > 1 ? (
    <RenderChart />
  ) : null;
};
const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(Sankey);
